import { useCallback } from 'react';
import { MediaDeviceInfo } from '@/hooks/use-media-devices';

interface DeviceSelectorProps {
  label: string;
  devices: MediaDeviceInfo[];
  selectedDevice: string | null;
  onSelectDevice: (deviceId: string) => void;
  isDisabled?: boolean;
  icon?: React.ReactNode;
}

/**
 * Device selector component for choosing video/audio devices
 */
export default function DeviceSelector({
  label,
  devices,
  selectedDevice,
  onSelectDevice,
  isDisabled = false,
  icon,
}: DeviceSelectorProps) {
  const handleDeviceChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const deviceId = event.target.value;
      if (deviceId) {
        onSelectDevice(deviceId);
      }
    },
    [onSelectDevice],
  );

  return (
    <div className="flex flex-col gap-2">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
        {icon}
        {label}
      </label>
      <select
        className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
        disabled={isDisabled || devices.length === 0}
        onChange={handleDeviceChange}
        value={selectedDevice || ''}
      >
        {devices.length === 0 ? (
          <option value="">No devices found</option>
        ) : (
          devices.map((device) => (
            <option key={device.deviceId} value={device.deviceId}>
              {device.label}
            </option>
          ))
        )}
      </select>
    </div>
  );
}
